import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  IpponCard,
  IpponContainer,
  IpponField,
  IpponLabel,
  IpponSingleCombobox,
  IpponText,
  IpponTitle,
  IpponVSpace,
} from '@ippon-ui/react';
import {
  LoaderComponent,
  LoadingErrorComponent,
  LoadingSkeleton,
  useLoader,
} from '@/generic/loader';
import { type Page, Pagination, PaginationComponent } from '@/generic/pagination';
import { inject } from '@/diagnostic/injections.ts';
import { DIAGNOSTICS } from '@/diagnostic/DiagnosticKeys.ts';
import type { DiagnosticSummary } from '@/diagnostic/domain/DiagnosticSummary.ts';
import type { DiagnosticId } from '@/diagnostic/domain/DiagnosticId.ts';
import { HistorySummaryComponent } from '@/diagnostic/primary/HistorySummaryComponent.tsx';
import { HistorySummaryLoadingComponent } from '@/diagnostic/primary/HistorySummaryLoadingComponent.tsx';
import { useDiagnosticReportModal } from '@/diagnostic/primary/UseDiagnosticReportModal.tsx';

const PAGE_SIZES = [5, 10, 20, 50] as const;

const PageSizeCombobox = ({
  pageSize,
  onChange,
}: {
  pageSize: number;
  onChange: (pageSize: number) => void;
}) => {
  const [query, setQuery] = useState('');
  const options = PAGE_SIZES.map((size) => ({ key: String(size), label: String(size) }));
  const selection = options.find((option) => option.key === String(pageSize));

  const handleSelect = (option: { key: string }) => {
    setQuery('');
    onChange(Number(option.key));
  };

  return (
    <IpponSingleCombobox
      id="diagnostic-history-page-page-size"
      dataSelector="diagnostic.history-page.page-size"
      labelledBy="diagnostic-history-page-page-size-label"
      options={options}
      query={query}
      onQueryChange={setQuery}
      selection={selection}
      onSelect={handleSelect}
    />
  );
};

const PageSizeField = ({
  pageSize,
  onChange,
}: {
  pageSize: number;
  onChange: (pageSize: number) => void;
}) => {
  const { t } = useTranslation('diagnostic');
  return (
    <IpponField>
      <IpponLabel
        htmlFor="diagnostic-history-page-page-size"
        id="diagnostic-history-page-page-size-label"
      >
        {t('diagnostic.history-page.page-size.label')}
      </IpponLabel>
      <PageSizeCombobox pageSize={pageSize} onChange={onChange} />
    </IpponField>
  );
};

const DiagnosticHistoryPageLoadingComponent = () => (
  <LoadingSkeleton>
    <IpponVSpace gap={16}>
      <IpponText tag="h3" variant="body" weight="bold" placeholder />
      <IpponVSpace gap={8}>
        <HistorySummaryLoadingComponent />
        <HistorySummaryLoadingComponent />
        <HistorySummaryLoadingComponent />
      </IpponVSpace>
    </IpponVSpace>
  </LoadingSkeleton>
);

const DiagnosticHistoryPageListComponent = ({
  page,
  onPageChange,
  onOpenReport,
}: {
  page: Page<DiagnosticSummary>;
  onPageChange: (page: number) => void;
  onOpenReport: (id: DiagnosticId) => void;
}) => {
  const { t } = useTranslation('diagnostic');

  if (page.totalElementsCount === 0) {
    return (
      <IpponText
        variant="body"
        color="neutral-tertiary"
        dataSelector="diagnostic.history-page.empty"
      >
        {t('diagnostic.history-page.empty')}
      </IpponText>
    );
  }

  return (
    <IpponVSpace gap={16}>
      <IpponText
        variant="body"
        weight="bold"
        tag="h3"
        dataSelector="diagnostic.history-page.results-count"
      >
        {t('diagnostic.history-page.results-count', { count: page.totalElementsCount })}
      </IpponText>
      <IpponVSpace gap={8}>
        {page.content.map((summary) => (
          <HistorySummaryComponent key={summary.id.value} summary={summary} onOpen={onOpenReport} />
        ))}
      </IpponVSpace>
      <PaginationComponent
        currentPage={page.currentPage}
        totalPages={page.totalPages}
        onPageChange={onPageChange}
        dataSelector="diagnostic.history-page.pagination"
      />
    </IpponVSpace>
  );
};

const DiagnosticHistoryListComponent = ({ pageSize }: { pageSize: number }) => {
  const [page, setPage] = useState(1);
  const [refreshes, setRefreshes] = useState(0);
  const { openReport, modal } = useDiagnosticReportModal();

  useEffect(() => inject(DIAGNOSTICS).onDiagnosticCompleted(() => setRefreshes((r) => r + 1)), []);

  const historyLoader = useLoader(
    () => inject(DIAGNOSTICS).historyPage(Pagination.of({ page, pageSize })),
    [page, pageSize, refreshes],
  );

  const onLoading = () => <DiagnosticHistoryPageLoadingComponent />;

  const onSuccess = (historyPage: Page<DiagnosticSummary>) => (
    <>
      <DiagnosticHistoryPageListComponent
        page={historyPage}
        onPageChange={setPage}
        onOpenReport={openReport}
      />
      {modal}
    </>
  );

  const onError = (error: Error) => (
    <IpponCard shadow="l3">
      <LoadingErrorComponent error={error} dataSelector="diagnostic.history-page.error" />
    </IpponCard>
  );

  return (
    <LoaderComponent
      loader={historyLoader}
      onLoading={onLoading}
      onSuccess={onSuccess}
      onError={onError}
    />
  );
};

export const DiagnosticHistoryPageComponent = () => {
  const { t } = useTranslation('diagnostic');
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);

  return (
    <IpponContainer>
      <IpponVSpace gap={32}>
        <IpponCard shadow="l3" size="large">
          <IpponVSpace gap={16}>
            <IpponTitle tag="h2" level={5} dataSelector="diagnostic.history-page.title">
              {t('diagnostic.history-page.title')}
            </IpponTitle>
            <PageSizeField pageSize={pageSize} onChange={setPageSize} />
          </IpponVSpace>
        </IpponCard>
        <DiagnosticHistoryListComponent key={pageSize} pageSize={pageSize} />
      </IpponVSpace>
    </IpponContainer>
  );
};
